import { Client, Functions } from 'appwrite'

export interface SupportRequestPayload {
  type: 'bug' | 'contact' | 'unlock_outreach' | 'contract_module'
  subject?: string
  message?: string
  pageUrl: string
  weeklyLeads?: string | number
  channels?: string[]
  /** contract_module: the module(s) requested for activation. */
  requestedModules?: string[]
  internalEstimates?: Record<string, unknown> | null
  userEmail?: string | null
  userName?: string | null
  clientInfo?: {
    id?: string | null
    name?: string | null
    email?: string | null
    hasOutreachModule?: boolean | null
  } | null
  impersonatedEmail?: string | null
}

interface SupportRequestResult {
  success: boolean
  message?: string
  error?: string
}

class SupportService {
  private client: Client
  private functions: Functions
  private functionId: string

  constructor() {
    this.client = new Client()
      .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
      .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '')

    this.functions = new Functions(this.client)
    this.functionId = 'n8n-proxy'
  }

  async sendRequest(payload: SupportRequestPayload): Promise<SupportRequestResult> {
    try {
      const effectiveUserEmail = payload.impersonatedEmail || payload.userEmail || payload.clientInfo?.email || ''
      const requestBody = {
        action: 'support_request',
        user_email: effectiveUserEmail,
        parameters: {
          type: payload.type,
          subject: payload.subject || '',
          message: payload.message || '',
          page_url: payload.pageUrl,
          weekly_leads: payload.weeklyLeads ?? '',
          channels: payload.channels ?? [],
          requested_modules: payload.requestedModules ?? [],
          internal_estimates: payload.internalEstimates ?? null,
          user_email: payload.userEmail || '',
          user_name: payload.userName || '',
          impersonated_email: payload.impersonatedEmail || '',
          client_info: payload.clientInfo || null,
          timestamp: new Date().toISOString(),
          source: 'lead-intelligence-app',
        },
      }

      const execution = await this.functions.createExecution(
        this.functionId,
        JSON.stringify(requestBody),
        false,
        '/',
        'POST'
      )

      const responseBody = JSON.parse(execution.responseBody || '{}')
      const responseData = responseBody.data || {}

      return {
        success: Boolean(responseBody.success),
        message: responseBody.message || responseData.message,
        error: responseBody.error,
      }
    } catch (error: any) {
      console.error('❌ Support request error:', error)
      return {
        success: false,
        error: error?.message || 'Failed to send support request',
      }
    }
  }
}

export const supportService = new SupportService()
